import { AUTH_STORAGE_KEYS } from "../constants/storageKeys";
import type { JwtPayload, TokenValidationResult } from "../types/auth.types";

/**
 * Decodes a JWT payload without verifying the signature.
 * Frontend decode is for UX only — the backend must verify tokens on every API call.
 */
export function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      return null;
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const json = atob(padded);

    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * Returns true when the token is a JWT with an `exp` claim in the past.
 * Non-JWT tokens (e.g. opaque API tokens) are treated as non-expired by JWT rules.
 */
export function isJwtExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) {
    return false;
  }

  const expiresAtMs = payload.exp * 1000;
  return Date.now() >= expiresAtMs;
}

/**
 * Extracts a role string from a JWT payload when present.
 */
export function extractRoleFromJwtPayload(payload: JwtPayload | null): string | null {
  if (!payload) {
    return null;
  }

  if (typeof payload.role === "string") {
    return payload.role;
  }

  if (Array.isArray(payload.user_role) && payload.user_role.length > 0) {
    return String(payload.user_role[0]);
  }

  if (typeof payload.user_role === "string") {
    return payload.user_role;
  }

  return null;
}

/**
 * Validates a stored token: decodes payload, checks JWT expiration.
 */
export function validateToken(token: string | null): TokenValidationResult {
  if (!token) {
    return {
      isValid: false,
      isExpired: false,
      payload: null,
      roleFromToken: null,
    };
  }

  const payload = decodeJwtPayload(token);
  const isExpired = isJwtExpired(token);

  if (isExpired) {
    return {
      isValid: false,
      isExpired: true,
      payload,
      roleFromToken: extractRoleFromJwtPayload(payload),
    };
  }

  return {
    isValid: true,
    isExpired: false,
    payload,
    roleFromToken: extractRoleFromJwtPayload(payload),
  };
}

/**
 * Removes all persisted authentication data from localStorage.
 */
export function clearAuthStorage(): void {
  localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.USER_DATA);
}

/**
 * Reads the authentication token from localStorage.
 */
export function getStoredToken(): string | null {
  return localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
}
